import React, { useState, useMemo } from 'react';
import { Calculator, ExternalLink, HelpCircle, Info, Zap, ArrowDownRight, ArrowUpRight, CheckCircle2 } from 'lucide-react';
import { Language } from '../i18n/translations';

interface SurchargeCalculatorCardProps {
  defaultKwh?: number;
  lang?: Language;
}

interface AreaPreset {
  id: string;
  nameJa: string;
  nameEn: string;
  lossRate: number; // e.g. 0.046 for 4.6%
  alpha: number;   // Lower threshold (yen/kWh)
  beta: number;    // Upper threshold (yen/kWh)
  defaultJepx: number;
}

const AREA_PRESETS: AreaPreset[] = [
  { id: 'kansai', nameJa: '関西電力エリア (Kansai)', nameEn: 'Kansai Area', lossRate: 0.045, alpha: 9.80, beta: 11.20, defaultJepx: 14.50 },
  { id: 'tokyo', nameJa: '東京電力エリア (Tokyo)', nameEn: 'Tokyo Area', lossRate: 0.046, alpha: 10.00, beta: 11.50, defaultJepx: 15.20 },
  { id: 'chubu', nameJa: '中部電力エリア (Chubu)', nameEn: 'Chubu Area', lossRate: 0.048, alpha: 9.90, beta: 11.40, defaultJepx: 14.80 },
  { id: 'kyushu', nameJa: '九州電力エリア (Kyushu)', nameEn: 'Kyushu Area', lossRate: 0.044, alpha: 9.50, beta: 10.90, defaultJepx: 12.60 },
  { id: 'tohoku', nameJa: '東北電力エリア (Tohoku)', nameEn: 'Tohoku Area', lossRate: 0.052, alpha: 10.10, beta: 11.60, defaultJepx: 15.80 },
  { id: 'chugoku', nameJa: '中国電力エリア (Chugoku)', nameEn: 'Chugoku Area', lossRate: 0.047, alpha: 9.70, beta: 11.10, defaultJepx: 14.20 },
  { id: 'shikoku', nameJa: '四国電力エリア (Shikoku)', nameEn: 'Shikoku Area', lossRate: 0.049, alpha: 9.80, beta: 11.30, defaultJepx: 13.90 },
  { id: 'hokuriku', nameJa: '北陸電力エリア (Hokuriku)', nameEn: 'Hokuriku Area', lossRate: 0.046, alpha: 9.60, beta: 11.00, defaultJepx: 13.50 },
  { id: 'hokkaido', nameJa: '北海道電力エリア (Hokkaido)', nameEn: 'Hokkaido Area', lossRate: 0.058, alpha: 10.50, beta: 12.00, defaultJepx: 16.50 },
];

export function SurchargeCalculatorCard({ defaultKwh = 171, lang = 'ja' }: SurchargeCalculatorCardProps) {
  const [selectedAreaId, setSelectedAreaId] = useState('tokyo');
  const [jepxAvg, setJepxAvg] = useState<number>(15.20);
  const [monthlyKwh, setMonthlyKwh] = useState<number>(defaultKwh);
  const [showFormulaDetails, setShowFormulaDetails] = useState(false);

  const activeArea = useMemo(() => {
    return AREA_PRESETS.find(a => a.id === selectedAreaId) || AREA_PRESETS[1];
  }, [selectedAreaId]);

  // Handle area change & sync defaults
  const handleAreaSelect = (areaId: string) => {
    setSelectedAreaId(areaId);
    const found = AREA_PRESETS.find(a => a.id === areaId);
    if (found) {
      setJepxAvg(found.defaultJepx);
    }
  };

  // SymEnergy Official Power Procurement Adjustment Formula (電源調達調整単価)
  // Tax 10% = 1.10
  // Loss Factor = (1 - lossRate)
  const calculation = useMemo(() => {
    const alpha = activeArea.alpha;
    const beta = activeArea.beta;
    const lossFactor = 1 - activeArea.lossRate;
    const taxRate = 1.10;

    let status: 'discount' | 'neutral' | 'surcharge' = 'neutral';
    let rawUnitPrice = 0; // yen / kWh

    if (jepxAvg < alpha) {
      status = 'discount'; // マイナス調整 (還元)
      // Formula: - ( (alpha - JEPX_avg) / (1 - lossRate) ) * 1.10
      rawUnitPrice = -((alpha - jepxAvg) / lossFactor) * taxRate;
    } else if (jepxAvg > beta) {
      status = 'surcharge'; // プラス調整 (請求)
      // Formula: + ( (JEPX_avg - beta) / (1 - lossRate) ) * 1.10
      rawUnitPrice = ((jepxAvg - beta) / lossFactor) * taxRate;
    } else {
      status = 'neutral';
      rawUnitPrice = 0;
    }

    // Round unit price to 2 decimal places (小数点第3位四捨五入)
    const unitPrice = Math.round(rawUnitPrice * 100) / 100;
    const totalSurcharge = Math.round(unitPrice * monthlyKwh);

    return {
      status,
      rawUnitPrice,
      unitPrice,
      totalSurcharge,
      alpha,
      beta,
      lossFactorPercent: (activeArea.lossRate * 100).toFixed(1)
    };
  }, [jepxAvg, monthlyKwh, activeArea]);

  const faqUrl = "https://faq.symenergy.net/%E9%9B%BB%E6%BA%90%E8%AA%BF%E9%81%94%E8%AA%BF%E6%95%B4%E5%8D%98%E4%BE%A1%E3%81%AE%E8%A8%88%E7%AE%97%E6%96%B9%E6%B3%95-680a7349fae7cd539c12767b";

  return (
    <div className="rounded-3xl bg-slate-900/90 border border-slate-800 p-5 shadow-2xl space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-gradient-to-tr from-amber-500/20 to-teal-500/20 border border-teal-500/30 text-teal-400 flex items-center justify-center shadow-inner">
            <Zap className="w-5 h-5 text-amber-400" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-white flex items-center gap-2">
              {lang === 'ja' ? '電源調達調整費 概算シミュレーター' : 'Power Procurement Surcharge Estimator'}
              <span className="text-[10px] font-normal text-teal-300 bg-teal-950 border border-teal-800 px-2 py-0.5 rounded-full font-mono">
                {lang === 'ja' ? '約款別表2準拠' : 'SymEnergy Formula'}
              </span>
            </h3>
            <p className="text-xs text-slate-400 mt-0.5">
              {lang === 'ja'
                ? 'JEPXスポット市場価格とエリア別託送損失率に基づく単価および概算調整額'
                : 'Estimate fuel & market adjustment surcharges based on JEPX area spot price'}
            </p>
          </div>
        </div>

        <a
          href={faqUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-teal-400 hover:text-teal-300 flex items-center gap-1 font-mono hover:underline bg-slate-950/80 px-2.5 py-1 rounded-xl border border-slate-800"
          title="SymEnergy FAQ Article"
        >
          <span>FAQ</span>
          <ExternalLink className="w-3 h-3" />
        </a>
      </div>

      {/* Control Inputs Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
        {/* Area Selection */}
        <div>
          <label className="text-[11px] font-semibold text-slate-400 block mb-1">
            {lang === 'ja' ? '対象電力エリア' : 'Target Region'}
          </label>
          <select
            value={selectedAreaId}
            onChange={(e) => handleAreaSelect(e.target.value)}
            className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs font-semibold text-white focus:outline-none focus:border-teal-500"
          >
            {AREA_PRESETS.map((a) => (
              <option key={a.id} value={a.id}>
                {lang === 'ja' ? a.nameJa : a.nameEn}
              </option>
            ))}
          </select>
        </div>

        {/* JEPX Average Price Input */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-[11px] font-semibold text-slate-400">
              {lang === 'ja' ? 'JEPXエリア平均単価' : 'JEPX Area Spot Avg'}
            </label>
            <span className="text-[10px] font-mono text-amber-400 font-bold">
              ¥{jepxAvg.toFixed(2)}/kWh
            </span>
          </div>
          <input
            type="number"
            step="0.1"
            min="0"
            max="50"
            value={jepxAvg}
            onChange={(e) => setJepxAvg(parseFloat(e.target.value) || 0)}
            className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs font-mono font-bold text-teal-300 focus:outline-none focus:border-teal-500"
          />
        </div>

        {/* Monthly Consumption kWh */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-[11px] font-semibold text-slate-400">
              {lang === 'ja' ? '月間使用電力量' : 'Monthly Usage'}
            </label>
            <span className="text-[10px] font-mono text-slate-300">
              {monthlyKwh} kWh
            </span>
          </div>
          <input
            type="number"
            step="1"
            min="0"
            max="2000"
            value={monthlyKwh}
            onChange={(e) => setMonthlyKwh(parseInt(e.target.value) || 0)}
            className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs font-mono font-bold text-white focus:outline-none focus:border-teal-500"
          />
        </div>
      </div>

      {/* JEPX Slider */}
      <div className="space-y-1 bg-slate-950/60 p-3 rounded-2xl border border-slate-800/80">
        <div className="flex justify-between text-[10px] font-mono text-slate-400">
          <span>{lang === 'ja' ? '還元ゾーン (<¥' + activeArea.alpha + ')' : 'Rebate (<¥' + activeArea.alpha + ')'}</span>
          <span className="text-slate-300 font-bold">{lang === 'ja' ? '調整基準範囲 (¥' + activeArea.alpha + '〜¥' + activeArea.beta + ')' : 'Neutral Zone'}</span>
          <span>{lang === 'ja' ? 'プラス調整 (>¥' + activeArea.beta + ')' : 'Surcharge (>¥' + activeArea.beta + ')'}</span>
        </div>
        <input
          type="range"
          min="5.0"
          max="25.0"
          step="0.1"
          value={jepxAvg}
          onChange={(e) => setJepxAvg(parseFloat(e.target.value))}
          className="w-full accent-teal-400 cursor-pointer h-1.5 bg-slate-800 rounded-lg"
        />
      </div>

      {/* Calculation Outcome Badge & Result Display */}
      <div className={`p-4 rounded-2xl border ${
        calculation.status === 'surcharge'
          ? 'bg-gradient-to-r from-amber-950/30 to-red-950/20 border-amber-800/50 text-amber-200'
          : calculation.status === 'discount'
          ? 'bg-gradient-to-r from-emerald-950/30 to-teal-950/20 border-emerald-800/50 text-emerald-200'
          : 'bg-slate-950/80 border-slate-800 text-slate-300'
      }`}>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              {calculation.status === 'surcharge' && <ArrowUpRight className="w-4 h-4 text-amber-400" />}
              {calculation.status === 'discount' && <ArrowDownRight className="w-4 h-4 text-emerald-400" />}
              {calculation.status === 'neutral' && <CheckCircle2 className="w-4 h-4 text-slate-400" />}

              <span className="text-xs font-bold font-mono">
                {calculation.status === 'surcharge' && (lang === 'ja' ? 'プラス調整（追加請求発生）' : 'Surcharge Applied')}
                {calculation.status === 'discount' && (lang === 'ja' ? 'マイナス調整（還元・割引適用）' : 'Rebate Applied')}
                {calculation.status === 'neutral' && (lang === 'ja' ? '基準範囲内（電源調達調整額 0円）' : 'Neutral Range (¥0)')}
              </span>
            </div>

            <div className="mt-1 flex items-baseline gap-3">
              <div>
                <span className="text-[10px] text-slate-400 block">{lang === 'ja' ? '電源調達調整単価' : 'Unit Price'}</span>
                <span className="text-lg font-bold font-mono tracking-tight">
                  {calculation.unitPrice > 0 ? `+¥${calculation.unitPrice.toFixed(2)}` : `¥${calculation.unitPrice.toFixed(2)}`} / kWh
                </span>
              </div>
              <div className="text-slate-600 font-mono text-sm">×</div>
              <div>
                <span className="text-[10px] text-slate-400 block">{lang === 'ja' ? '使用量' : 'Usage'}</span>
                <span className="text-sm font-bold font-mono text-white">{monthlyKwh} kWh</span>
              </div>
            </div>
          </div>

          <div className="text-left sm:text-right w-full sm:w-auto pt-2 sm:pt-0 border-t sm:border-t-0 border-slate-800">
            <span className="text-[10px] font-semibold text-slate-400 block">
              {lang === 'ja' ? '概算電源調達調整額' : 'Estimated Total Adjustment'}
            </span>
            <span className={`text-2xl font-black font-mono tracking-tight ${
              calculation.totalSurcharge > 0
                ? 'text-amber-400'
                : calculation.totalSurcharge < 0
                ? 'text-emerald-400'
                : 'text-white'
            }`}>
              {calculation.totalSurcharge > 0 ? `+¥${calculation.totalSurcharge.toLocaleString()}` : `¥${calculation.totalSurcharge.toLocaleString()}`}
            </span>
          </div>
        </div>
      </div>

      {/* Accordion / Expandable Official Formula Details */}
      <div>
        <button
          type="button"
          onClick={() => setShowFormulaDetails(!showFormulaDetails)}
          className="text-xs font-semibold text-slate-400 hover:text-teal-300 flex items-center gap-1 transition-colors"
        >
          <Info className="w-3.5 h-3.5" />
          <span>
            {lang === 'ja' ? 'SymEnergy 電源調達調整単価の計算式・定数解説' : 'SymEnergy Formula Explanation'}
          </span>
        </button>

        {showFormulaDetails && (
          <div className="mt-2.5 p-3 rounded-2xl bg-slate-950 border border-slate-800/80 text-[11px] font-mono space-y-2 text-slate-300 leading-relaxed">
            <div className="text-teal-400 font-bold border-b border-slate-800 pb-1 flex justify-between">
              <span>{lang === 'ja' ? '【計算方式: 2026/11検針予定日分〜適用約款】' : '[Formula: Applicable from Nov 2026 Meter Readings]'}</span>
              <span>{lang === 'ja' ? `託送損失率: ${calculation.lossFactorPercent}%` : `Loss Rate: ${calculation.lossFactorPercent}%`}</span>
            </div>

            <div className="space-y-1">
              <p className="text-slate-200">
                <span className="text-emerald-400 font-bold">{lang === 'ja' ? '① 還元 (マイナス調整)' : '① Rebate (Negative Adjustment)'}</span> [{lang === 'ja' ? `JEPX < 基準単価α(¥${activeArea.alpha})` : `JEPX < Base Rate α (¥${activeArea.alpha})`}]:
              </p>
              <p className="pl-3 text-slate-400">
                {lang === 'ja' ? '単価' : 'Unit Rate'} = - ( ( {activeArea.alpha} - {jepxAvg.toFixed(2)} ) ÷ (1 - {activeArea.lossRate}) ) × 1.10 = <span className="text-emerald-300 font-bold">¥{calculation.unitPrice.toFixed(2)} / kWh</span>
              </p>

              <p className="text-slate-200 pt-1">
                <span className="text-amber-400 font-bold">{lang === 'ja' ? '② 請求 (プラス調整)' : '② Surcharge (Positive Adjustment)'}</span> [{lang === 'ja' ? `JEPX > 基準単価β(¥${activeArea.beta})` : `JEPX > Base Rate β (¥${activeArea.beta})`}]:
              </p>
              <p className="pl-3 text-slate-400">
                {lang === 'ja' ? '単価' : 'Unit Rate'} = + ( ( {jepxAvg.toFixed(2)} - {activeArea.beta} ) ÷ (1 - {activeArea.lossRate}) ) × 1.10 = <span className="text-amber-300 font-bold">¥{calculation.unitPrice.toFixed(2)} / kWh</span>
              </p>
            </div>

            <div className="pt-1 text-[10px] text-slate-500 border-t border-slate-800/60">
              {lang === 'ja'
                ? '※ 単価の小数点第3位は四捨五入して算定されます。沖縄電力エリアは本調整費の対象外（従来型燃料費調整額を適用）となります。'
                : '* Unit rates are rounded to 2 decimal places. Okinawa area is excluded from this adjustment (standard fuel cost adjustments apply).'}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
